// livestreamUtils.js
'use strict';
import * as MessageTypes from '../serviceWorker/AppMessageTypes.js';
import {
  LivestreamProvider,
  PRESET_SD,
  ExclusivePrivateStatus,
  SIC_BO_CAMPAIGN_NAME,
} from '../resource/liveStreamConstants.js';

import { breakpoint } from '../style/variables.js';

/**
 * Get can unlock livestream show by ticket.
 * @param {string} {productId} - product id.
 * @param {string} {productPrice} - product price.
 * @returns {Boolean}
 */
export const getCanUnlockByTicket = ({ productId, productPrice } = {}) => {
  const hasProductId = productId !== null && productId !== undefined;
  const hasProductPrice = productPrice !== null && productPrice !== undefined;
  if (hasProductId && hasProductPrice) return true;
  return false;
};

/**
 * Get is waiting product data.
 * @param {string} {productId} - product id.
 * @param {string} {productPrice} - product price.
 * @returns {Boolean}
 */
export const getIsWaitingProductData = ({ productId, productPrice } = {}) => {
  const hasProductId = productId !== null && productId !== undefined;
  const hasProductPrice = productPrice !== null && productPrice !== undefined;
  if (hasProductId && !hasProductPrice) return true;
  if (productId === undefined) return true;
  return false;
};

/**
 * Determines if the stream provider is BytePlus.
 * @param {object} options - The options object.
 * @param {string} options.provider - The stream provider identifier.
 * @returns {boolean} True if the provider is BytePlus, false otherwise.
 */
export const getIsByteplusStreamProvider = ({ provider } = {}) =>
  LivestreamProvider.BYTEPLUS === provider;

/**
 * Determines if the stream provider is Agora.
 * @param {object} options - The options object.
 * @param {string} options.provider - The stream provider identifier.
 * @returns {boolean} True if the provider is Agora, false otherwise.
 */
export const getIsAgoraStreamProvider = ({ provider } = {}) =>
  LivestreamProvider.AGORA === provider;

/**
 * Determines if the stream provider support DRM.
 * @param {object} options - The options object.
 * @param {string} options.provider - The stream provider identifier.
 * @returns {boolean} True if the stream provider support DRM, false otherwise.
 */
export const getIsStreamProviderSupportDrm = ({ provider } = {}) =>
  // Only SWAG support DRM now.
  LivestreamProvider.SWAG === provider;

/**
 * Format goal breakdown.
 * @param {object} goalBreakdown - The goal breakdown.
 * @returns {object} The formatted goal breakdown.
 */
export const formatGoalBreakdown = (goalBreakdown = {}) =>
  Object.keys(goalBreakdown).reduce((breakdown, key) => {
    breakdown[key] = {
      id: key,
      progress: goalBreakdown[key],
    };
    return breakdown;
  }, {});

/**
 * Get should show balance warning.
 * @param {object} options - The options object.
 * @param {string} options.preset - The stream preset.
 * @param {string} options.fundingId - The funding id.
 * @param {number} options.streamTokenExp - The stream token expiration unix timestamp.
 * @param {number} options.priceDuration - The price duration.
 * @param {number} options.balance - The balance.
 * @param {number} options.price - The price.
 * @param {number} options.remainingBalanceMultiplier - The remaining balance multiplier.
 * @param {number} options.nowUnix - The current unix timestamp.
 * @returns {boolean} True if the balance warning should be shown, false otherwise.
 */
export const getShouldShowBalanceWarning = ({
  preset = null,
  fundingId = null,
  streamTokenExp = 0,
  priceDuration = 0,
  balance = 0,
  price = 0,
  remainingBalanceMultiplier = 0,
  nowUnix = 0,
} = {}) => {
  const tokenAvailableMin = streamTokenExp
    ? Math.floor((streamTokenExp - nowUnix) / 60)
    : 0;
  return (
    preset === PRESET_SD &&
    !fundingId &&
    streamTokenExp &&
    priceDuration === 1 &&
    balance < price * remainingBalanceMultiplier &&
    tokenAvailableMin < remainingBalanceMultiplier
  );
};

/**
 * Determines if the stream provider is Tencent.
 * @param {object} options - The options object.
 * @param {string} options.provider - The stream provider identifier.
 * @returns {boolean} True if the provider is Tencent, false otherwise.
 */
export const getIsTencentStreamProvider = ({ provider } = {}) =>
  LivestreamProvider.TENCENT === provider;

/**
 * Grid config matching TopHomeLiveStreamFeed's LiveStreamCardsWrapper CSS:
 * - grid-template-columns: repeat(auto-fill, minmax(MIN_CARD_WIDTH, 1fr))
 * - gap: GAP
 * - padding: 0 16px (total 32px)
 */
const TOP_LIVESTREAM_FEED_GRID_CONFIG = {
  padding: 32,
  desktop: { minCardWidth: 320, gap: 12, rows: 1 },
  laptop: { minCardWidth: 280, gap: 12, rows: 1 },
  tablet: { minCardWidth: 200, gap: 8, rows: 2 },
  mobile: { minCardWidth: 160, gap: 8, rows: 2 },
};

/**
 * Get top feed count based on screen width
 * Calculates how many cards fit in the top feed section based on CSS Grid layout
 * @param {object} params
 * @param {number} params.width - screen width
 * @return {number} top feed count (cards per row * rows)
 */
export const getTopLivestreamCountByWidth = ({ width } = {}) => {
  const innerWidth =
    width ??
    (typeof window !== 'undefined' ? window.innerWidth : breakpoint.mobile);

  const config =
    innerWidth > breakpoint.laptop
      ? TOP_LIVESTREAM_FEED_GRID_CONFIG.desktop
      : innerWidth > breakpoint.tablet
        ? TOP_LIVESTREAM_FEED_GRID_CONFIG.laptop
        : innerWidth > breakpoint.mobile
          ? TOP_LIVESTREAM_FEED_GRID_CONFIG.tablet
          : TOP_LIVESTREAM_FEED_GRID_CONFIG.mobile;

  const containerWidth = innerWidth - TOP_LIVESTREAM_FEED_GRID_CONFIG.padding;
  const cardsPerRow =
    Math.floor(
      (containerWidth + config.gap) / (config.minCardWidth + config.gap)
    ) || 1;

  return cardsPerRow * config.rows;
};

/**
 * Determines if the session is rated.
 * @param {object} options - The options object.
 * @param {number} options.ratedAs - The rated as.
 * @returns {boolean} True if the session is rated, false otherwise.
 */
export const getIsRated = ({ ratedAs = undefined } = {}) => {
  // `ratedAs` might be `null` or `0` to `100`.
  // `undefined` means not rated yet
  const isRated = ratedAs !== undefined;
  return isRated;
};

/**
 * Build the full set of stream-related Pusher channel infos keyed off the
 * stream/user IDs only — runtime subscribe gating (sic_bo active, exclusive
 * performing, etc.) is *not* applied here. Each info carries an
 * `isExpectedToSubscribe(args)` predicate that `getExpectedLivestreamChannelInfos`
 * uses to filter to the subset that should actually be subscribed right now.
 *
 * Modal/debug surfaces that want the full fixed list call this; the health
 * checker calls `getExpectedLivestreamChannelInfos` instead.
 *
 * @param {object} params
 * @param {string} [params.streamerUserId]
 * @param {string} [params.activeStreamId]
 * @param {string} [params.meId]
 * @returns {Array<{channelName: string, subscribeMessageType: string, subscribePayload: object, isExpectedToSubscribe: (args: object) => boolean}>}
 *   Infos for every stream-related channel that *could* be subscribed
 *   given the provided IDs.
 * @example
 * getAllLivestreamChannelInfos({ streamerUserId: 'A', activeStreamId: 'A', meId: 'M' });
 * // => [
 * //   { channelName: 'private-enc-user@A', ..., isExpectedToSubscribe },
 * //   { channelName: 'private-enc-stream@A', ..., isExpectedToSubscribe },
 * //   { channelName: 'private-enc-user-v2@M', ..., isExpectedToSubscribe },
 * //   { channelName: 'private-enc-campaign@sic_bo', ..., isExpectedToSubscribe },
 * //   { channelName: 'presence-enc-stream-exclusive@A', ..., isExpectedToSubscribe },
 * // ]
 */
export const getAllLivestreamChannelInfos = ({
  streamerUserId,
  activeStreamId,
  meId,
} = {}) => {
  const infos = [];

  if (streamerUserId) {
    infos.push({
      channelName: `private-enc-user@${streamerUserId}`,
      subscribeMessageType: MessageTypes.USER_ONLINE_STATUS,
      subscribePayload: { userId: streamerUserId, shouldRetry: true },
      isExpectedToSubscribe: () => true,
    });
  }

  if (activeStreamId) {
    infos.push({
      channelName: `private-enc-stream@${activeStreamId}`,
      subscribeMessageType: MessageTypes.SUBSCRIBE_STREAM_CHANNEL,
      subscribePayload: { streamId: activeStreamId },
      isExpectedToSubscribe: ({ shouldSubscribeStreamChannel } = {}) =>
        !!shouldSubscribeStreamChannel,
    });
  }

  if (meId) {
    infos.push({
      channelName: `private-enc-user-v2@${meId}`,
      subscribeMessageType: MessageTypes.SUBSCRIBE_PRIVATE_USER_V2_CHANNEL,
      subscribePayload: { userId: meId },
      isExpectedToSubscribe: () => true,
    });
  }

  infos.push({
    channelName: `private-enc-campaign@${SIC_BO_CAMPAIGN_NAME}`,
    subscribeMessageType: MessageTypes.SUBSCRIBE_CAMPAIGN_CHANNEL,
    subscribePayload: { campaignName: SIC_BO_CAMPAIGN_NAME },
    isExpectedToSubscribe: ({ isSicBoCampaignActive } = {}) =>
      !!isSicBoCampaignActive,
  });

  if (activeStreamId) {
    infos.push({
      channelName: `presence-enc-stream-exclusive@${activeStreamId}`,
      subscribeMessageType:
        MessageTypes.SUBSCRIBE_PRESENCE_STREAM_EXCLUSIVE_CHANNEL,
      subscribePayload: { streamId: activeStreamId },
      isExpectedToSubscribe: ({
        exclusivePrivateStatus,
        isExclusivePrivateBuyer,
        isLivestreamModerator,
      } = {}) =>
        ExclusivePrivateStatus.PERFORMING === exclusivePrivateStatus &&
        (isExclusivePrivateBuyer || isLivestreamModerator),
    });
  }

  return infos;
};

/**
 * Build the list of livestream Pusher channels that should be subscribed for
 * the current active stream — i.e. the subset of `getAllLivestreamChannelInfos`
 * whose per-info `isExpectedToSubscribe` predicate passes for the given
 * runtime conditions.
 *
 * @param {object} params
 * @param {string} [params.activeStreamId]
 * @param {string} [params.streamerUserId]
 * @param {string} [params.meId]
 * @param {boolean} [params.shouldSubscribeStreamChannel]
 * @param {string} [params.exclusivePrivateStatus]
 * @param {boolean} [params.isExclusivePrivateBuyer]
 * @param {boolean} [params.isLivestreamModerator]
 * @param {boolean} [params.isSicBoCampaignActive]
 * @returns {Array<{channelName: string, subscribeMessageType: string, subscribePayload: object}>}
 *   Infos for every channel that should currently be subscribed.
 * @example
 * getExpectedLivestreamChannelInfos({
 *   activeStreamId: 'A', streamerUserId: 'A', meId: 'M',
 *   shouldSubscribeStreamChannel: true,
 *   exclusivePrivateStatus: null, isExclusivePrivateBuyer: false,
 *   isLivestreamModerator: false, isSicBoCampaignActive: false,
 * });
 * // => [
 * //   { channelName: 'private-enc-user@A', ... },
 * //   { channelName: 'private-enc-stream@A', ... },
 * //   { channelName: 'private-enc-user-v2@M', ... },
 * // ]
 */
export const getExpectedLivestreamChannelInfos = (args = {}) =>
  getAllLivestreamChannelInfos(args).filter(({ isExpectedToSubscribe }) =>
    isExpectedToSubscribe(args)
  );
